import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Sparkles, PenLine } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const toggle = (arr, x) => (arr.includes(x) ? arr.filter((a) => a !== x) : [...arr, x]);

const Chip = ({ on, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`text-sm rounded-full px-3 py-1.5 border transition-colors ${on ? "bg-teal text-white border-transparent" : "bg-white text-navy border-line hover:border-teal"}`}>
    {children}
  </button>
);

export const TeamDialog = ({ open, onOpenChange, team, gifts, onSave }) => {
  const [f, setF] = useState({ name: "", description: "", gifts: [], disc: [] });
  const [ai, setAi] = useState("");
  const pro = useAuth().church?.billing?.pro;
  const assist = async (mode) => {
    setAi(mode);
    try {
      const { data } = await api.post("/ai/team-assist", { name: f.name, description: f.description, mode });
      setF((p) => (mode === "write" ? { ...p, description: data.description } : { ...p, gifts: data.gifts, disc: data.disc }));
      toast.success(mode === "write" ? "Description drafted" : "Profile suggested");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI is unavailable right now");
    } finally {
      setAi("");
    }
  };

  useEffect(() => {
    if (open) setF(team ? { name: team.name, description: team.description, gifts: team.gifts, disc: team.disc } : { name: "", description: "", gifts: [], disc: [] });
  }, [open, team]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="team-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{team ? "Edit team" : "New team"}</DialogTitle></DialogHeader>
        <div className="space-y-5 py-2">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="team-name-input" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Description</Label>
              {pro && <button type="button" disabled={!f.name.trim() || !!ai} onClick={() => assist("write")} className="chip-btn flex items-center gap-1.5" data-testid="ai-help-write-btn">
                <PenLine size={13} /> {ai === "write" ? "Writing..." : "Help me write"}
              </button>}
            </div>
            <Textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="team-description-input" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Gifts this team leans on</Label>
              {pro && <button type="button" disabled={!f.name.trim() || !!ai} onClick={() => assist("profile")} className="chip-btn flex items-center gap-1.5" data-testid="ai-suggest-profile-btn">
                <Sparkles size={13} /> {ai === "profile" ? "Thinking..." : "AI Suggest profile"}
              </button>}
            </div>
            <div className="flex flex-wrap gap-2">
              {gifts.map((g) => (
                <Chip key={g.key} on={f.gifts.includes(g.key)} onClick={() => setF({ ...f, gifts: toggle(f.gifts, g.key) })} testid={`team-gift-${g.key}`}>{g.name}</Chip>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label>DISC styles that thrive here</Label>
            <div className="flex gap-2">
              {["D", "I", "S", "C"].map((d) => (
                <Chip key={d} on={f.disc.includes(d)} onClick={() => setF({ ...f, disc: toggle(f.disc, d) })} testid={`team-disc-${d}`}>{d}</Chip>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <button className="btn-ghost !py-2" onClick={() => onOpenChange(false)} data-testid="team-cancel-btn">Cancel</button>
          <button className="btn-navy !py-2" disabled={!f.name.trim()} onClick={() => onSave(f)} data-testid="team-save-btn">Save team</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
